import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton, Popover, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../../../../../../../../../containers/LoadingOverlay/LoadingOverlay";
import DialogTitle from "../../../../../../../../../containers/DialogTitle/DialogTitle";
import { intlFormatDateShort } from "../../../../../../../../../utils/time-utils";
import { ActionButtons, Container, MainContainer, DetailsContainner } from "./styles";
import { TravelExpensesApiService } from "../../../../../../../../../services/travel-expenses/travel-expenses-api.service";
import { TravelExpenseSTO } from "../../../../../../../../../models/travel-expenses/travel-expense-sto";
import { toastifyResponse } from "../../../../../../../../../utils/toastify-response/toastify-response";
import { TravelExpenseRef } from "../../../../../../../../../models/travel-expenses";
import DialogActions from "../../../../../../../../../containers/DialogActions/DialogActions";

interface Props {
  item: TravelExpenseRef;
  handleNotifyParent?: () => void;
}

interface State {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  loading: boolean;
  error: string;
  isDialogOpen: boolean;
  confirmed: boolean;
  status: boolean | null;
}
const AuditStatusChange = ({ item, handleNotifyParent }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.manage_travel_and_expenses.tables.expense_reports.expense_report_details";

  const [state, setState] = useState<State>({
    open: false,
    anchorEl: null,
    isDialogOpen: false,
    loading: false,
    error: "",
    confirmed: false,
    status: null,
  });

  const handleClose = useCallback(() => {
    setState((state) => ({
      ...state,
      isDialogOpen: false,
      loading: false,
      status: false,
      error: "",
      confirmed: false,
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    setState((state) => ({ ...state, confirmed: true }));
  }, []);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const itemToSave = { ...item, auditAccepted: state.status };

      const response = await new TravelExpensesApiService().saveTravelExpenseAuditStatus([
        itemToSave as TravelExpenseSTO,
      ]);

      if (!response.error) {
        setState((state) => ({
          ...state,
          open: false,
          anchorEl: null,
          isDialogOpen: false,
          loading: false,
          error: "",
          confirmed: false,
          status: null,
        }));

        handleNotifyParent && handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [item, handleNotifyParent, state.status]);

  useEffect(() => {
    state.confirmed && handleSave();
  }, [handleSave, state.confirmed]);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <IconButton
        onClick={(event) => {
          event.stopPropagation();

          setState((state) => ({
            ...state,
            open: true,
            anchorEl: event.currentTarget as HTMLButtonElement,
          }));
        }}
        sx={{ height: "20px", width: "20px" }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </IconButton>

      <Popover
        open={state.open}
        anchorEl={state.anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={() => {
          setState((state) => ({ ...state, open: false }));
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            alignItems: "flex-start",
            gap: "5px",
          },
          onClick: (event) => {
            event.stopPropagation();
          },
        }}
      >
        <ActionButtons
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true, status: true }));
          }}
          disabled={item.auditAccepted === true}
        >
          Accept
        </ActionButtons>
        <ActionButtons
          reject
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true, status: false }));
          }}
          disabled={item.auditAccepted === false}
        >
          Reject
        </ActionButtons>
      </Popover>

      <Dialog
        open={state.isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={`Update expense receipt audit status`}
            handleClose={handleClose}
          />
          <DialogContent>
            <MainContainer>
              <Container>
                <DetailsContainner>
                  <Typography>
                    <>
                      <b>{`Receipt for: `}</b>
                    </>
                    <>{`${item.type?.name} (${intlFormatDateShort(new Date(item?.date))})`}</>
                  </Typography>
                  <Typography>
                    <>
                      <b>{`${t(`${tRP}.columns.amount`)}: `}</b>
                    </>
                    <>{`${item.amount?.toLocaleCustom()} ${item?.currency?.code}`}</>
                  </Typography>
                  <Typography>
                    <>
                      <b>{`${t(`${tRP}.columns.converted_amount`)}: `}</b>
                    </>
                    <>{`${item.convertedAmount?.toLocaleCustom()} ${item?.convertedAmountCurrency?.code ?? ""}`}</>
                  </Typography>
                  <Typography>
                    <>
                      <b>{`${t(`${tRP}.columns.description`)}: `}</b>
                    </>
                    <>{item.description}</>
                  </Typography>
                </DetailsContainner>

                <Typography sx={{ fontSize: "18px" }}>
                  <b>Are you sure you want to {`${state.status ? "approve" : "reject"}`} this receipt?</b>
                </Typography>
              </Container>
            </MainContainer>
          </DialogContent>

          <DialogActions
            handleSave={handleConfirm}
            handleCancel={handleClose}
            error={state.error}
            customSaveName={t("buttons.confirm")}
            useGoback
          />
        </LoadingOverlay>
      </Dialog>
    </div>
  );
};
export default AuditStatusChange;
